<script setup lang="ts">
import { useIntervalFn, useSwipe } from '@vueuse/core';
import { onMounted, ref, useTemplateRef, watch } from 'vue';

import ChevronIcon from '@/components/base/assets/ChevronIcon.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';

const nutsFamilyAlbum = nutshell['static_img/about-us/slideshow/00_nuts_family_album.jpg'];
const theMarket = nutshell['static_img/about-us/slideshow/05_the_market.jpg'];
const poppySolIn1942 = nutshell['static_img/about-us/slideshow/10_poppy_sol_in_1942.jpg'];
const poppySolInsideFirstStore =
  nutshell['static_img/about-us/slideshow/15_poppy_sol_inside_first_store.jpg'];
const outsideTheOldStore = nutshell['static_img/about-us/slideshow/20_outside_the_old_store.jpg'];
const poppySolOutsideFirstStore =
  nutshell['static_img/about-us/slideshow/25_poppy_sol_outside_first_store.jpg'];
const kennyAndUncleSandy = nutshell['static_img/about-us/slideshow/30_kenny_and_uncle_sandy.jpg'];
const standingOutsideTheStore =
  nutshell['static_img/about-us/slideshow/35_standing_outside_the_store.jpg'];
const goodies = nutshell['static_img/about-us/slideshow/40_goodies.jpg'];
const constructionOnTheSecondStore =
  nutshell['static_img/about-us/slideshow/45_construction_on_the_second_store.jpg'];
const mulberryStStorefront =
  nutshell['static_img/about-us/slideshow/50_mulberry_st_storefront.jpg'];
const kiddingAroundOutsideTheStore =
  nutshell['static_img/about-us/slideshow/55_kidding_around_outside_the_store.jpg'];
const poppyAtTheCashRegister =
  nutshell['static_img/about-us/slideshow/60_poppy_at_the_cash_register.jpg'];
const kennyAtTheCashRegister =
  nutshell['static_img/about-us/slideshow/65_kenny_at_the_cash_register.jpg'];
const poppySolInTheFacesOfNewark =
  nutshell['static_img/about-us/slideshow/70_poppy_sol_in_the_faces_of_newark.jpg'];
const jeffreyAndDavid = nutshell['static_img/about-us/slideshow/75_jeffrey_and_david.jpg'];
const theFamily = nutshell['static_img/about-us/slideshow/80_the_family.jpg'];
const uncleSandyDavidJeffreyKenny =
  nutshell['static_img/about-us/slideshow/85_uncle_sandy_david_jeffrey_kenny.jpg'];
const outsideOurWarehouseToday =
  nutshell['static_img/about-us/slideshow/90_outside_our_warehouse_today.jpg'];

const images = [
  { alt: 'Nuts.com family album', content: nutsFamilyAlbum },
  { alt: 'The market', content: theMarket },
  { alt: 'Poppy Sol in 1942', content: poppySolIn1942 },
  { alt: 'Poppy Sol inside the first store', content: poppySolInsideFirstStore },
  { alt: 'Outside of the old store', content: outsideTheOldStore },
  { alt: 'Poppy Sol outside the first store', content: poppySolOutsideFirstStore },
  { alt: 'Kenny and uncle Sandy', content: kennyAndUncleSandy },
  { alt: 'Outside of the store', content: standingOutsideTheStore },
  { alt: 'Goodies', content: goodies },
  { alt: 'Construction on the second store', content: constructionOnTheSecondStore },
  { alt: 'Mulberry St store front', content: mulberryStStorefront },
  {
    alt: 'People playing around outside the store',
    content: kiddingAroundOutsideTheStore,
  },
  { alt: 'Poppy at the cash register', content: poppyAtTheCashRegister },
  { alt: 'Kenny at the cash register', content: kennyAtTheCashRegister },
  { alt: 'Poppy Sol in the faces of Newark', content: poppySolInTheFacesOfNewark },
  { alt: 'Jeffrey and David', content: jeffreyAndDavid },
  { alt: 'The family', content: theFamily },
  {
    alt: 'Uncle Sandy, David, Jeffrey and Kenny',
    content: uncleSandyDavidJeffreyKenny,
  },
  { alt: 'Outside Nuts.com warehouse today', content: outsideOurWarehouseToday },
];

const currentSlide = ref(0);

const nextSlide = () => {
  if (currentSlide.value >= images.length - 1) {
    currentSlide.value = 0;
  } else {
    currentSlide.value += 1;
  }
};

const previousSlide = () => {
  if (currentSlide.value <= 0) {
    currentSlide.value = images.length - 1;
  } else {
    currentSlide.value -= 1;
  }
};

const swipeAboutUsRoot = useTemplateRef('swipeAboutUsRoot');
const { direction } = useSwipe(swipeAboutUsRoot);

watch(direction, (value) => {
  if (value === 'left') {
    previousSlide();
  } else if (value === 'right') {
    nextSlide();
  }
});

const { pause, resume } = useIntervalFn(nextSlide, 4000, { immediate: false });
onMounted(resume);
</script>

<template>
  <div
    class="relative flex items-center justify-center w-full overflow-hidden h-96"
    ref="swipeAboutUsRoot"
    @focusin="pause"
    @focusout="resume"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <UnstyledButton
      aria-label="Previous slide"
      class="left-0 flex items-center justify-center rounded-full carousel-control lg:hover:bg-white/25 lg:bg-white/40"
      data-test="previous-slide-button"
      @click="previousSlide"
    >
      <ChevronIcon direction="left" :size="20" class="text-neutral-500 lg:text-neutral-400" />
    </UnstyledButton>
    <div
      v-for="(image, index) in images"
      :key="image.content"
      class="flex items-center justify-center w-full h-full truncate lg:w-64"
      :class="{
        'fancy-slide-in': currentSlide === index,
        'fancy-slide-out z-0 invisible': currentSlide !== index,
      }"
    >
      <img
        :alt="image.alt"
        class="object-contain"
        :fetchpriority="index === 0 ? 'high' : 'low'"
        :src="image.content"
        tabindex="0"
      />
    </div>
    <UnstyledButton
      aria-label="Next slide"
      class="right-0 flex items-center justify-center rounded-full carousel-control lg:hover:bg-white/25 lg:bg-white/40"
      data-test="next-slide-button"
      @click="nextSlide"
    >
      <ChevronIcon direction="right" :size="20" class="text-neutral-500 lg:text-neutral-400" />
    </UnstyledButton>
  </div>
</template>

<style scoped>
.fancy-slide-out {
  animation: slide-out 200ms;
  animation-fill-mode: forwards;
}

@keyframes slide-out {
  from {
    opacity: 0;
  }
  to {
    opacity: 0;
    display: none;
  }
}

.fancy-slide-in {
  animation: slide-in 400ms;
  animation-fill-mode: backwards;
}

@keyframes slide-in {
  from {
    transform: translateX(200%);
    opacity: 0.25;
  }
  to {
    transform: translateX(0%);
    opacity: 1;
  }
}

.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
}
</style>
